import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import * as m from '../../../localization/out/messages.js';
import { addInterviewSlots, getJobPositionByID } from '../../api/job-position.js';

/**
 * A reusable component to display time slots grouped by day
 */
export const TimeSlotsDisplay = stateful((props: { timeSlots: TimeSlot[]; jobPositionID?: string }) => {
  const [localSlots, setLocalSlots] = useState(props.timeSlots ?? [], [props.timeSlots]);
  const [pending, setPending] = useState(false);
  const [request] = useState({ pending: false }, []);
  // Group time slots by day for simple display. TimeSlot.start/end are
  // Date|undefined; coerce to Date to be defensive in case legacy data
  // serialized them as ISO strings.
  const groupedByDay = localSlots.reduce(
    (acc, s) => {
      const start = s?.start ? new Date(s.start) : null;
      const end = s?.end ? new Date(s.end) : null;
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return acc;
      const label = format(start, 'dd.MM.yyyy');
      (acc[label] ??= []).push({ start, end });
      return acc;
    },
    {} as Record<string, Array<{ start: Date; end: Date }>>,
  );

  // Sort slots within each day
  Object.values(groupedByDay).forEach(items => items.sort((a, b) => a.start.getTime() - b.start.getTime()));

  const addSlot = async (day: string, position: 'before' | 'after') => {
    if (!props.jobPositionID || request.pending) return;
    const items = groupedByDay[day];
    const reference = position === 'before' ? items?.[0] : items?.[items.length - 1];
    if (!reference) return;
    const duration = reference.end.getTime() - reference.start.getTime();
    if (duration <= 0) return;
    const start = position === 'before' ? new Date(reference.start.getTime() - duration) : reference.end;
    const end = new Date(start.getTime() + duration);
    request.pending = true;
    setPending(true);
    try {
      const result = await addInterviewSlots(
        props.jobPositionID,
        [new TimeSlot({ start, end })],
        [],
      )({ added: 1, skipped: 1, removed: 1 });
      const refreshed = await getJobPositionByID(props.jobPositionID)({
        _id: 1,
        timeSlots: selectionSet(() => TimeSlot, 1),
      });
      if (!refreshed) throw new Error(m.job_form_slot_generation_refresh_error());
      setLocalSlots((refreshed.timeSlots ?? []).map(slot => new TimeSlot(slot)));
      void XSnackbar.show(
        m.job_form_slot_generation_result({ added: result.added, skipped: result.skipped, removed: result.removed }),
      );
    } catch (error) {
      void XSnackbar.show(error instanceof Error ? error.message : m.job_form_slot_generation_error_alert());
    } finally {
      request.pending = false;
      setPending(false);
    }
  };

  if (Object.keys(groupedByDay).length === 0) {
    return html`<div class="text-sm text-base-content/70">${m.time_slots_display_empty()}</div>`;
  }

  return html`
    <div class="flex flex-wrap gap-4">
      ${Object.entries(groupedByDay).map(
        ([label, items]) => html`
          <div class="card bg-base-200 shadow-sm">
            <div class="card-body p-4">
              <h3 class="card-title text-base">
                ${m.time_slots_display_day()} <span class="font-normal">${label}</span>
              </h3>
              ${
                props.jobPositionID
                  ? html` <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      ?disabled=${pending}
                      @click=${() => addSlot(label, 'before')}
                    >
                      <span class="icon-[material-symbols--add]"></span>${m.time_slots_display_add_before()}
                    </button>`
                  : nothing
              }
              <div class="mt-2 flex flex-col gap-1">
                ${items.map(
                  s => html`<div class="text-sm">${format(s.start, 'HH:mm')} - ${format(s.end, 'HH:mm')}</div>`,
                )}
              </div>
              ${
                props.jobPositionID
                  ? html` <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      ?disabled=${pending}
                      @click=${() => addSlot(label, 'after')}
                    >
                      <span class="icon-[material-symbols--add]"></span>${m.time_slots_display_add_after()}
                    </button>`
                  : nothing
              }
            </div>
          </div>
        `,
      )}
    </div>
  `;
});
